import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import * as api from '../utils/accountApi';
import { ApiError, newRequestId } from '../utils/api';
import { applySubscriptionAction } from '../core/rules/subscription';
import { useSite } from './SiteContext';
import { passwordProblem } from '../core/rules/password';
import { EMAIL_ERROR, MAGIC_LINK_PARAM, RESET_PARAM, SIGNED_OUT_ERROR, StaleSessionError, VERIFY_PARAM, failure, messageOf, upsert, validEmail, type AuthResult } from '../utils/accountSession';
import { startWithLink } from '../utils/magicLinkStart';
import type { AskTopic } from '../utils/askSarah';
import type { Account } from '../core/types/account';
import type { RegisterClaim } from '../core/types/backend';
import type { AppointmentRequest, CustomerPatch, SubscriptionAction } from '../core/types/requests';

/**
 * Mijn omgeving: sessie en het klantaccount.
 *
 * Alle wijzigingen lopen via benoemde acties, nooit via "zet het hele account".
 *
 * Optimistisch, maar met de server als bron:
 * - `confirmed` is de laatste stand die de server bevestigde;
 * - `pending` zijn acties die nog onderweg zijn, elk als functie (geen kopie van het account);
 * - wat de klant ziet is altijd `pending` toegepast op `confirmed`.
 *
 * Mislukt één actie, dan valt alleen die actie weg; andere acties die nog onderweg zijn
 * blijven zichtbaar. Daarna halen we het account opnieuw op, zodat de stand gegarandeerd
 * weer gelijk is aan de server. Verzoeken gaan één voor één naar de server, in de volgorde
 * waarin de klant ze deed, zodat "overslaan" en daarna "uitstellen" nooit omgekeerd aankomen.
 */

// AuthResult en de pure hulpfuncties staan in utils/accountSession.ts; het type blijft hier ook te importeren.
export type { AuthResult } from '../utils/accountSession';

/** Draait één abonnementsactie terug. `false` als dat niet (meer) kan; de melding komt uit de context. */
export type UndoFn = () => Promise<boolean>;
type Status = 'loading' | 'ready';
type RegisterInput = {
  firstName: string;
  email: string;
  password: string;
  /** Direct na afrekenen: de zojuist geplaatste bestelling. */
  claim?: RegisterClaim;
};
type AccountValue = {
  status: Status;
  account: Account | null;
  signedIn: boolean;
  signIn: (email: string, password: string) => Promise<AuthResult>;
  sendMagicLink: (email: string) => Promise<AuthResult>;
  /** De inloglink uit de mail openen (in de demo is het token het e-mailadres). */
  openMagicLink: (token: string) => Promise<AuthResult>;
  /** Waarom de inloglink uit de mail niet werkte (verlopen, al gebruikt, geen verbinding); `null` = niets te melden. */
  linkNotice: string | null;
  dismissLinkNotice: () => void;
  requestReset: (email: string) => Promise<AuthResult>;
  /** Token uit de herstelmail (`?herstel=`), zolang de klant nog geen nieuw wachtwoord koos. */
  resetToken: string | null;
  /** Alleen de demo-hint: doet alsof de klant op de link in de herstelmail klikt. */
  openResetLink: (token: string) => void;
  /** Nieuw wachtwoord met het token uit de mail. Bij succes is ze ingelogd. */
  confirmPasswordReset: (password: string) => Promise<AuthResult>;
  /** Het herstelscherm verlaten (bijv. om een nieuwe link aan te vragen). */
  cancelPasswordReset: () => void;
  /** Wachtwoord instellen (zonder `currentPassword`) of wijzigen. */
  changePassword: (input: {
    currentPassword?: string;
    password: string;
  }) => Promise<AuthResult>;
  register: (input: RegisterInput) => Promise<AuthResult>;
  signOut: () => void;
  /** De bevestigingsmail opnieuw sturen naar het huidige adres. */
  resendVerification: () => Promise<AuthResult>;
  /** Abonnement: één actie per keer. `true` als het gelukt is. */
  subscription: (action: SubscriptionAction) => Promise<boolean>;
  /**
   * Zelfde actie, maar met "ongedaan maken": geeft bij succes een functie terug die precies
   * déze actie terugdraait (via het `undoToken` van de server), `null` als het mislukte.
   * De site stuurt nooit zelf een oude stand mee: de server bewaart die.
   */
  changeSubscription: (action: SubscriptionAction) => Promise<UndoFn | null>;
  /** Opnieuw ophalen, bijv. na een bestelling in de webshop. */
  refresh: () => Promise<void>;
  /** Afspraken */
  requestAppointment: (req: Omit<AppointmentRequest, 'requestId'>) => Promise<boolean>;
  rescheduleAppointment: (id: string, date: string, time: string) => Promise<boolean>;
  cancelAppointment: (id: string) => Promise<boolean>;
  /** Fotodagboek. `storageConsentVersion` alleen als ze nu (opnieuw) toestemming geeft. */
  addDiaryEntry: (photo: Blob, note: string, storageConsentVersion?: string) => Promise<boolean>;
  /** Toestemming intrekken: alle foto's verwijderen. */
  deleteDiary: () => Promise<boolean>;
  /** Gegevens, voorkeuren en toestemmingen */
  updateCustomer: (patch: CustomerPatch) => Promise<boolean>;
  /**
   * Een formulier in Gegevens bewaren: wacht op de server en geeft de fout terug (geen toast), zodat
   * het formulier hem bij het veld kan tonen. Een ander e-mailadres kan 423 `reauth_required` geven.
   */
  saveProfile: (patch: CustomerPatch) => Promise<AuthResult>;
  /** AVG: alles wat bewaard is als bestand downloaden. */
  downloadData: () => Promise<AuthResult>;
  /** AVG: account verwijderen. Bij succes is ze uitgelogd. */
  deleteAccount: (confirmEmail: string) => Promise<AuthResult>;
};

/**
 * Drie contexts, net als in StoreContext:
 * - AccountContext: sessie, account en acties (verandert bij elke accountwijziging);
 * - SignedInContext: alleen "ingelogd ja/nee" — de header luistert hiernaar en tekent
 *   dus niet opnieuw bij een nieuwe dagboekfoto of een verzet abonnement;
 * - AskSarah: de lade is UI-state en hoort niet in dezelfde waarde als het account.
 */
type AskState = {
  askOpen: boolean;
  askTopic: AskTopic;
};
type AskActions = {
  openAsk: (topic?: AskTopic) => void;
  closeAsk: () => void;
};
type Pending = {
  apply: (a: Account) => Account;
};
const AccountContext = createContext<AccountValue | null>(null);
const SignedInContext = createContext<boolean | null>(null);
const AskStateContext = createContext<AskState | null>(null);
const AskActionsContext = createContext<AskActions | null>(null);
export function AccountProvider({
  children
}: {
  children: ReactNode;
}) {
  const [status, setStatus] = useState<Status>('loading');
  const [account, setAccount] = useState<Account | null>(null);
  // Abonnementsregels uit de actuele instellingen (useSite), via een ref zodat acties stabiel blijven.
  const subscriptionPolicy = useSite().policy.subscription;
  const subscriptionPolicyRef = useRef(subscriptionPolicy);
  subscriptionPolicyRef.current = subscriptionPolicy;
  const [resetToken, setResetToken] = useState<string | null>(null);
  const [linkNotice, setLinkNotice] = useState<string | null>(null);
  const [ask, setAsk] = useState<AskState>({
    askOpen: false,
    askTopic: 'routine'
  });
  const askActions = useMemo<AskActions>(() => ({
    openAsk: (topic = 'routine') => setAsk({
      askOpen: true,
      askTopic: topic
    }),
    closeAsk: () => setAsk(s => ({
      ...s,
      askOpen: false
    }))
  }), []);

  /** Laatste stand die de server bevestigde. */
  const confirmed = useRef<Account | null>(null);
  /** Acties die nog onderweg zijn, in volgorde. */
  const pending = useRef<Pending[]>([]);
  /** Eén rij voor alle schrijfacties: de server krijgt ze in de volgorde van de klant. */
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  /** Verandert bij in- en uitloggen: antwoorden uit een vorige sessie worden genegeerd. */
  const session = useRef(0);

  /** Wat de klant ziet: de bevestigde stand met alle acties die nog onderweg zijn erbovenop. */
  const show = useCallback(() => {
    const base = confirmed.current;
    setAccount(base ? pending.current.reduce((a, p) => p.apply(a), base) : null);
  }, []);

  /** Nieuwe stand van de server. `newSession` bij in- of uitloggen: alles wat nog onderweg was, telt niet meer. */
  const confirm = useCallback((a: Account | null, opts: {
    newSession?: boolean;
  } = {}) => {
    if (opts.newSession || !a) {
      session.current += 1;
      pending.current = [];
    }
    confirmed.current = a;
    show();
  }, [show]);

  /**
   * Achter de rij aansluiten: start pas als alle eerdere schrijfacties klaar zijn (geslaagd of niet).
   * Is de sessie intussen gewisseld (uitloggen, 401, inloggen), dan wordt het verzoek NIET meer
   * verstuurd: anders zou een actie van de vorige klant met het cookie van de nieuwe sessie bij de
   * server aankomen. Alleen een verzoek dat al onderweg was, kan niet meer terug; dat antwoord
   * wordt genegeerd in `mutate` en `reconcile`.
   */
  const enqueue = useCallback(<T,>(run: () => Promise<T>): Promise<T> => {
    const at = session.current;
    const next = queue.current.then(() => {
      if (session.current !== at) throw new StaleSessionError();
      return run();
    });
    queue.current = next.catch(() => undefined);
    return next;
  }, []);

  /** Haal de serverstand op ná alle acties in de rij; daarna klopt de stand gegarandeerd. */
  const reconcile = useCallback(async () => {
    const at = session.current;
    try {
      const fresh = await enqueue(() => api.fetchMe());
      if (session.current === at) confirm(fresh);
    } catch {
      /* geen verbinding: de bevestigde stand blijft staan */
    }
  }, [enqueue, confirm]);
  useEffect(() => {
    let alive = true;
    // Komt de klant binnen via de inloglink uit de mail (?inloglink=<token>), dan eerst dat token inwisselen.
    // Zelfde voor de bevestigingslink (?verifieer=<token>) en de herstellink (?herstel=<token>).
    // Alle tokens meteen uit de adresbalk (geschiedenis, schermdelen, Referer).
    const url = new URL(window.location.href);
    const token = url.searchParams.get(MAGIC_LINK_PARAM);
    const verifyToken = url.searchParams.get(VERIFY_PARAM);
    const reset = url.searchParams.get(RESET_PARAM);
    if (token || verifyToken || reset !== null) {
      url.searchParams.delete(MAGIC_LINK_PARAM);
      url.searchParams.delete(VERIFY_PARAM);
      url.searchParams.delete(RESET_PARAM);
      window.history.replaceState(window.history.state, '', url.toString());
    }
    // Het herstel-token wordt pas ingewisseld als ze een nieuw wachtwoord kiest; een lege `?herstel=` telt als verlopen.
    if (reset !== null) setResetToken(reset.trim());
    const verified = verifyToken ? api.verifyEmail({
      token: verifyToken
    }).then(() => toast.success('Je e-mailadres is bevestigd', {
      description: 'Dank je! Je account is nu helemaal compleet.'
    })).catch((err: unknown) => toast.error('Bevestigen lukte niet', {
      description: messageOf(err)
    })) : Promise.resolve();
    // Na het bevestigen pas het account ophalen, zodat de melding "bevestig je adres" meteen weg is.
    // Werkt de inloglink niet, dan kijken we nog of ze al ingelogd was; zo niet, dan zegt het
    // inlogscherm waarom (nooit stil een leeg inlogscherm na een klik in de mail).
    // De regel (met tests) staat in utils/magicLinkStart.ts.
    void verified.then(() => startWithLink(token, t => api.verifyMagicLink({
      token: t
    }), api.fetchMe)).then(({
      account: a,
      notice
    }) => {
      if (!alive) return;
      confirm(a, {
        newSession: true
      });
      if (notice) setLinkNotice(notice);
    }).catch(() => alive && confirm(null)).finally(() => alive && setStatus('ready'));
    return () => {
      alive = false;
    };
  }, [confirm]);

  // Eenmaal binnen (op welke manier ook) is de melding over de oude inloglink achterhaald.
  useEffect(() => {
    if (account) setLinkNotice(null);
  }, [account]);
  const auth = useCallback(async (fn: () => Promise<Account | {
    ok: true;
  }>): Promise<AuthResult> => {
    try {
      const result = await fn();
      if ('customer' in result) confirm(result, {
        newSession: true
      });
      return {
        ok: true
      };
    } catch (err) {
      return failure(err);
    }
  }, [confirm]);

  /**
   * Optimistische mutatie.
   * - `optimistic`: wat de actie met het account doet. Wordt opnieuw toegepast als de basis
   *   verandert (een andere actie slaagt, of een verse serverstand), dus het moet een functie
   *   van het account zijn, nooit een vaste kopie.
   * - `remote`: het verzoek. Wacht in de rij tot eerdere verzoeken klaar zijn.
   * - `commit`: verwerkt het antwoord in de bevestigde stand. Zonder `commit` telt `optimistic`.
   */
  const mutate = useCallback(async <T,>(optimistic: (a: Account) => Account, remote: () => Promise<T>, commit?: (a: Account, result: T) => Account): Promise<boolean> => {
    if (!confirmed.current) return false;
    const at = session.current;
    const op: Pending = {
      apply: optimistic
    };
    pending.current = [...pending.current, op];
    show();
    const done = () => {
      pending.current = pending.current.filter(p => p !== op);
    };
    try {
      const result = await enqueue(remote);
      if (session.current !== at) return false;
      done();
      if (confirmed.current) confirmed.current = commit ? commit(confirmed.current, result) : optimistic(confirmed.current);
      show();
      return true;
    } catch (err) {
      if (session.current !== at) return false;
      // Alleen deze actie valt weg; wat er verder onderweg is, blijft staan.
      done();
      show();
      if (err instanceof ApiError && err.status === 401) {
        toast.error('Je bent uitgelogd', {
          description: 'Log opnieuw in om verder te gaan.'
        });
        confirm(null);
        return false;
      }
      toast.error('Dat lukte niet', {
        description: messageOf(err)
      });
      // De server kan het verzoek deels verwerkt hebben: haal de echte stand op.
      void reconcile();
      return false;
    }
  }, [show, enqueue, confirm, reconcile]);
  const value = useMemo<AccountValue>(() => {
    /** Terugdraaien wacht op de server (geen optimistische gok): alleen hij kent de vorige stand. */
    const undoSubscription = (undoToken: string) => mutate(a => a, () => api.undoSubscription({
      undoToken
    }), (a, res) => ({
      ...a,
      subscription: res.subscription
    }));
    const changeSubscription = async (action: SubscriptionAction): Promise<UndoFn | null> => {
      // Het token hoort bij déze actie: een oude melding draait nooit een latere actie terug.
      let undoToken: string | null = null;
      const ok = await mutate(a => ({
        ...a,
        subscription: applySubscriptionAction(a.subscription, action, subscriptionPolicyRef.current)
      }), () => api.subscriptionAction(action), (a, res) => {
        undoToken = res.undoToken;
        return {
          ...a,
          subscription: res.subscription
        };
      });
      if (!ok) return null;
      return () => undoToken ? undoSubscription(undoToken) : Promise.resolve(false);
    };
    const subscription = async (action: SubscriptionAction) => (await changeSubscription(action)) !== null;
    return {
      status,
      account,
      signedIn: Boolean(account),
      signIn: (email, password) => {
        if (!validEmail(email)) return Promise.resolve({
          ok: false,
          error: EMAIL_ERROR
        });
        return auth(() => api.login({
          email: email.trim(),
          password
        }));
      },
      sendMagicLink: async email => {
        if (!validEmail(email)) return {
          ok: false,
          error: EMAIL_ERROR
        };
        const result = await auth(() => api.sendMagicLink({
          email: email.trim()
        }));
        if (result.ok) setLinkNotice(null);
        return result;
      },
      openMagicLink: token => auth(() => api.verifyMagicLink({
        token: token.trim()
      })),
      linkNotice,
      dismissLinkNotice: () => setLinkNotice(null),
      requestReset: email => validEmail(email) ? auth(() => api.requestPasswordReset({
        email: email.trim()
      })) : Promise.resolve({
        ok: false,
        error: EMAIL_ERROR
      }),
      resetToken,
      openResetLink: token => setResetToken(token.trim()),
      confirmPasswordReset: async password => {
        if (!resetToken) return {
          ok: false,
          error: 'Deze link is verlopen of al gebruikt.',
          code: 'link_expired'
        };
        // Zelfde regel als bij registreren; de server controleert hem opnieuw (plus gelekte wachtwoorden).
        const weak = passwordProblem(password, {
          email: account?.customer.email,
          firstName: account?.customer.firstName
        });
        if (weak) return {
          ok: false,
          error: weak,
          fields: {
            password: weak
          }
        };
        const result = await auth(() => api.confirmPasswordReset({
          token: resetToken,
          password
        }));
        if (result.ok) {
          setResetToken(null);
          toast.success('Je nieuwe wachtwoord is opgeslagen');
        }
        return result;
      },
      cancelPasswordReset: () => setResetToken(null),
      changePassword: async ({
        currentPassword,
        password
      }) => {
        const c = confirmed.current?.customer;
        if (!c) return {
          ok: false,
          error: SIGNED_OUT_ERROR
        };
        if (c.hasPassword && !currentPassword) {
          const msg = 'Vul je huidige wachtwoord in.';
          return {
            ok: false,
            error: msg,
            fields: {
              currentPassword: msg
            }
          };
        }
        const weak = passwordProblem(password, {
          email: c.email,
          firstName: c.firstName
        });
        if (weak) return {
          ok: false,
          error: weak,
          fields: {
            password: weak
          }
        };
        const at = session.current;
        try {
          // In de rij, zodat het na eerdere wijzigingen (bijv. een nieuw e-mailadres) bij de server aankomt.
          await enqueue(() => api.changePassword(c.hasPassword ? {
            currentPassword,
            password
          } : {
            password
          }));
          if (session.current === at && confirmed.current) {
            confirmed.current = {
              ...confirmed.current,
              customer: {
                ...confirmed.current.customer,
                hasPassword: true
              }
            };
            show();
          }
          return {
            ok: true
          };
        } catch (err) {
          if (err instanceof ApiError && err.status === 401) {
            confirm(null);
            return {
              ok: false,
              error: SIGNED_OUT_ERROR
            };
          }
          return failure(err);
        }
      },
      register: ({
        firstName,
        email,
        password,
        claim
      }) => {
        if (!firstName.trim()) return Promise.resolve({
          ok: false,
          error: 'Hoe mogen we je noemen?'
        });
        if (!validEmail(email)) return Promise.resolve({
          ok: false,
          error: EMAIL_ERROR
        });
        const weak = passwordProblem(password, {
          email,
          firstName
        });
        if (weak) return Promise.resolve({
          ok: false,
          error: weak
        });
        return auth(() => api.register({
          requestId: newRequestId(),
          firstName: firstName.trim(),
          email: email.trim(),
          password,
          claimOrderId: claim?.order.id
        }, claim));
      },
      signOut: () => {
        confirm(null);
        void api.logout();
      },
      resendVerification: () => auth(() => api.resendVerification()),
      subscription,
      changeSubscription,
      refresh: reconcile,
      requestAppointment: req => {
        // Eén id per aanvraag, ook als de rij hem later pas verstuurt.
        const requestId = newRequestId();
        return mutate(a => a, () => api.requestAppointment({
          requestId,
          ...req
        }), (a, appt) => ({
          ...a,
          appointments: upsert(a.appointments, appt)
        }));
      },
      rescheduleAppointment: (id, date, time) => mutate(a => ({
        ...a,
        appointments: a.appointments.map(x => x.id === id ? {
          ...x,
          date,
          time,
          status: 'aanvraag'
        } : x)
      }), () => api.rescheduleAppointment(id, {
        date,
        time
      }), (a, appt) => ({
        ...a,
        appointments: upsert(a.appointments, appt)
      })),
      cancelAppointment: id => mutate(a => ({
        ...a,
        appointments: a.appointments.filter(x => x.id !== id)
      }), () => api.cancelAppointment(id)),
      addDiaryEntry: (photo, note, storageConsentVersion) => mutate(a => a, () => api.uploadDiaryEntry({
        photo,
        note,
        storageConsentVersion
      }), (a, entry) => ({
        ...a,
        customer: storageConsentVersion ? {
          ...a.customer,
          photoStorageConsent: storageConsentVersion
        } : a.customer,
        diary: upsert(a.diary, entry)
      })),
      deleteDiary: () => mutate(a => ({
        ...a,
        customer: {
          ...a.customer,
          photoStorageConsent: null,
          diaryConsent: false
        },
        diary: []
      }), () => api.deleteDiary()),
      downloadData: async () => {
        try {
          const data = await api.exportData();
          const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], {
            type: 'application/json'
          }));
          const link = document.createElement('a');
          link.href = url;
          link.download = `mijn-gegevens-beauty-bewust-${data.exportedAt.slice(0, 10)}.json`;
          link.click();
          // Even laten staan: sommige browsers starten de download pas na deze tik.
          window.setTimeout(() => URL.revokeObjectURL(url), 1000);
          return {
            ok: true
          };
        } catch (err) {
          return {
            ok: false,
            error: messageOf(err)
          };
        }
      },
      deleteAccount: async confirmEmail => {
        try {
          await enqueue(() => api.deleteAccount({
            confirmEmail
          }));
          confirm(null);
          return {
            ok: true
          };
        } catch (err) {
          return failure(err);
        }
      },
      updateCustomer: patch => mutate(a => ({
        ...a,
        // Een nieuw adres is pas bevestigd als de server dat zegt.
        customer: {
          ...a.customer,
          ...patch,
          emailVerified: patch.email && patch.email !== a.customer.email ? false : a.customer.emailVerified
        }
      }), () => api.updateCustomer(patch), (a, customer) => ({
        ...a,
        customer
      })),
      saveProfile: async patch => {
        if (!confirmed.current) return {
          ok: false,
          error: SIGNED_OUT_ERROR
        };
        const at = session.current;
        try {
          const customer = await enqueue(() => api.updateCustomer(patch));
          if (session.current !== at) return {
            ok: false,
            error: SIGNED_OUT_ERROR
          };
          if (confirmed.current) confirmed.current = {
            ...confirmed.current,
            customer
          };
          show();
          return {
            ok: true
          };
        } catch (err) {
          if (session.current === at && err instanceof ApiError && err.status === 401) confirm(null);
          return failure(err);
        }
      }
    };
  }, [status, account, resetToken, linkNotice, auth, mutate, confirm, reconcile, enqueue, show]);
  return <AccountContext.Provider value={value} data-id="element-5735" data-mp="1ooza70t3ncoa">
      <SignedInContext.Provider value={value.signedIn} data-id="element-5736" data-mp="22p0601uibtww">
        <AskActionsContext.Provider value={askActions} data-id="element-5737" data-mp="22oh05ji4svz8">
          <AskStateContext.Provider value={ask} data-id="element-5738" data-mp="1ynezcy8kgbv5">{children}</AskStateContext.Provider>
        </AskActionsContext.Provider>
      </SignedInContext.Provider>
    </AccountContext.Provider>;
}
const need = <T,>(v: T | null, name: string): T => {
  if (v === null) throw new Error(`${name} moet binnen <AccountProvider> staan`);
  return v;
};
export const useAccount = () => need(useContext(AccountContext), 'useAccount');
/** Alleen "ingelogd ja/nee": voor de header en andere plekken buiten Mijn omgeving. */
export const useSignedIn = () => need(useContext(SignedInContext), 'useSignedIn');
/** Is de Vraag Sarah-lade open, en met welk onderwerp. Alleen de lade zelf heeft dit nodig. */
export const useAskSarah = () => need(useContext(AskStateContext), 'useAskSarah');
/** Stabiele functies om de lade te openen of te sluiten: knoppen tekenen nooit opnieuw door de lade. */
export const useAskSarahActions = () => need(useContext(AskActionsContext), 'useAskSarahActions');

/** Voor schermen achter de login: het account is er altijd. */
export function useSignedInAccount() {
  const ctx = useAccount();
  if (!ctx.account) throw new Error('Geen account: dit scherm hoort achter de login');
  return {
    ...ctx,
    account: ctx.account
  };
}